<template>
    <v-dialog v-model="showDialog" persistent max-width="900px" max-height="100%">
      <v-card class="omr-edit-dialog">
        <v-card-title class="headline d-flex justify-space-between align-center">
          Edit OMR Responses
          <v-btn icon @click="closeDialog" class="right-btn cust-btn" color="secondary">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
  
        <v-card-text>
          <v-container v-if="omrResponseData" style="padding-top: 5px !important; padding-bottom: 5px !important;">
            <v-row class="omr-edit-dialog-row">
              <v-col cols="12" v-if="showWarning">
                <div class="warning-text">{{ warningText }}</div>
              </v-col>
              <v-col cols="6">
                <div class="d-flex align-center mb-2">
                  <div class="mr-4">
                    <strong>Test ID:</strong>
                    <template v-if="isTestIdEditing">
                      <v-text-field v-model="omrResponseData.TestId" dense outlined type="number" hide-details
                        class="d-inline-block ml-2 test-id-field" @input="handleTestIdChange"></v-text-field>
                      <v-icon class="ml-2 mr-2" small @click="saveTestId" color="success"
                        style="vertical-align: baseline;">mdi-check</v-icon>
                      <v-icon class="ml-2 mr-2" small @click="cancelTestIdEdit" color="error"
                        style="vertical-align: baseline;">mdi-close</v-icon>
                    </template>
                    <template v-else>
                      <span class="ml-2 mr-2">{{ omrResponseData.TestId || 'N/A' }}</span>
  
                      <v-tooltip left bottom content-class="cust-new-tooltip-bottom">
                        <template v-slot:activator="{ on }">
                          <v-icon small @click="startTestIdEdit" color="primary" style="vertical-align: baseline;"
                            v-on="on">mdi-pencil</v-icon>
                        </template>
                        <span>Click here to edit the Test ID. Changing the Test ID will disable all other fields. If you
                          want to edit OMR
                          responses for a different Test ID, please update the Test ID first and save. Once the Test ID
                          update is complete, you
                          can continue editing OMR responses.</span>
                      </v-tooltip>
  
  
  
                    </template>
                  </div>
                </div>
              </v-col>
              <v-col cols="6" class="text-right" v-if="selectedOmr">
                <strong>Student Name:</strong> {{ selectedOmr.student_name || 'N/A' }}
              </v-col>
            </v-row>
  
            <v-row class="omr-edit-dialog-row">
              <v-col cols="6">
                <div class="d-flex align-center mb-2">
                  <div class="mr-4" v-if="!haveStudentIDOnly">
                    <strong>Roll Number:</strong>
                    <v-text-field v-model="omrResponseData.Roll" dense outlined type="number" hide-details
                      class="d-inline-block ml-2" :disabled="isFieldsDisabled"></v-text-field>
                  </div>
                  <div class="mr-4" v-else>
                    <strong>Student Id:</strong>
                    <v-text-field v-model="omrResponseData.Roll" dense outlined type="number" hide-details
                      class="d-inline-block ml-2" :disabled="isFieldsDisabled"></v-text-field>
                  </div>
                </div>
              </v-col>
              <v-col cols="6" class="text-right">
                <strong>Test Name:</strong> {{ testName || 'N/A' }}
              </v-col>
            </v-row>
  
            <v-row class="omr-edit-dialog-row">
              <v-col cols="6">
                <div class="d-flex align-center mb-2">
                  <div>
                    <strong>Set:</strong>
                    <div class="d-inline-flex ml-2">
                      <label v-for="(option, index) in ['A', 'B', 'C', 'D']" :key="option"
                        :class="['set-radio-label', { 'status-disabled': isFieldsDisabled }]">
                        <input type="radio" :name="'set'" :value="(index + 1).toString()" v-model="omrResponseData.Set"
                          class="set-radio-input" :disabled="isFieldsDisabled">
                        <span class="set-radio-text">{{ option }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
  
            <!-- OMR Questions Grid -->
            <v-row class="omr-detail-popup">
              <v-col cols="3" v-for="section in 4" :key="section">
                <div class="omr-section">
                  <div v-for="qNum in getQuestionsForSection(section)" :key="qNum"
                    :class="['question-row', !isMultipleChoiceQuestion(`q${qNum}`) ? 'question-number-row' : '']">
                    <div class="question-number">{{ qNum }}.</div>
                    <div class="radio-group" v-if="isMultipleChoiceQuestion(`q${qNum}`)">
                      <label v-for="option in ['A', 'B', 'C', 'D']" :key="option"
                        :class="['checkbox-label', { 'status-disabled': isFieldsDisabled }]">
                        <input type="checkbox" :name="`q${qNum}`" :value="option"
                          :checked="isOptionSelected(qNum, option)" @change="toggleOption(qNum, option)"
                          class="checkbox-input" :disabled="isFieldsDisabled">
                        <span class="checkbox-text">{{ option }}</span>
                      </label>
                    </div>
                    <div v-else class="numeric-input">
                      <v-text-field v-model="omrResponseData[`q${qNum}`]" dense outlined type="number" hide-details
                        class="numeric-question-input" :disabled="isTestIdEdited"></v-text-field>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <div v-else class="text-center pa-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-tooltip top content-class="cust-new-tooltip-bottom"
            v-if="selectedOmr && selectedOmr.job_status !== 'N' && selectedOmr.job_status !== 'I'">
            <template v-slot:activator="{ on }">
              <v-btn @click="updateOmrResponses" :loading="updatingOmr" class="right-btn cust-btn" color="primary"
                v-on="on">
                Update OMR Responses
              </v-btn>
            </template>
            <span>Click here to update OMR responses</span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>
  
  <script>
  import { mapActions } from "vuex";
  
  export default {
    name: "OmrEditPopup",
    props: {
      value: Boolean,
      omrId: {
        type: [String, Number]
      }
    },
    data() {
      return {
        omrResponseData: null,
        updatingOmr: false,
        templateData: null,
        originalTestId: null,
        isTestIdEdited: false,
        isTestIdEditing: false,
        tempTestId: null,
        selectedOmr: null,
        testData: null,
        haveStudentIDOnly: false
      };
    },
    computed: {
      showDialog: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        }
      },
      testName() {
        return this.testData?.test_name || '';
      },
      isFieldsDisabled() {
        if (!this.selectedOmr) return false;
        return this.selectedOmr.job_status === 'N' ||
          this.selectedOmr.job_status === 'I' ||
          this.isTestIdEdited;
      },
      warningText() {
        if (this.selectedOmr.job_status === 'N' || this.selectedOmr.job_status === 'I') {
          return "OMR response is currently disabled as processing is in progress. Please wait until the process is complete to update.";
        } else if (this.isTestIdEdited) {
          return "Changing the Test ID will disable all other fields. If you want to edit OMR responses for a different Test ID, please update the Test ID first and save. Once the Test ID update is complete, you can continue editing OMR responses.";
        }
        return "";
      },
      showWarning() {
        return this.isFieldsDisabled;
      },
    },
    methods: {
      ...mapActions({
        getOmrDetailsById: "testStore/getOmrDetailsById",
        editOmrResponseData: "testStore/editOmrResponseData",
      }),
  
      async fetchOmrData() {
        try {
          this.$root.showLoader = true;
          this.haveStudentIDOnly = false;
          // this.omrResponseData = {"q1": "AD", "q2": "AD", "q3": "A", "q4": "A", "q5": "D", "q6": "B", "q7": "D", "q8": "C", "q9": "", "Set": "4", "q10": "D", "q11": "", "q12": "D", "q13": "", "q14": "C", "q15": "", "q16": "C", "q17": "D", "q18": "B", "q19": "", "q20": "B", "q21": "600", "q22": "3", "q23": "25", "q24": "4", "q25": "48", "q26": "4", "q27": "3", "q28": "1", "q29": "", "q30": "150", "q31": "C", "q32": "D", "q33": "", "q34": "B", "q35": "", "q36": "C", "q37": "B", "q38": "B", "q39": "D", "q40": "C", "q41": "", "q42": "", "q43": "", "q44": "C", "q45": "", "q46": "", "q47": "", "q48": "", "q49": "", "q50": "", "q51": "27", "q52": "12", "q53": "3", "q54": "1", "q55": "", "q56": "0", "q57": "12", "q58": "48", "q59": "60", "q60": "4", "q61": "", "q62": "D", "q63": "", "q64": "C", "q65": "", "q66": "C", "q67": "", "q68": "D", "q69": "C", "q70": "", "q71": "", "q72": "B", "q73": "", "q74": "C", "q75": "", "q76": "D", "q77": "", "q78": "B", "q79": "", "q80": "D", "q81": "", "q82": "", "q83": "", "q84": "", "q85": "5", "q86": "", "q87": "", "q88": "2", "q89": "4", "q90": "5", "Roll": 42003, "TestId": "0000000489"}
          // Then get the detailed OMR data
          const response = await this.getOmrDetailsById(this.omrId);
          this.selectedOmr = response;
          this.testData = {
            test_id: response.test_id,
            test_name: response.test_name
          };
          if (response.omr_processed_json) {
            let processedData = typeof response.omr_processed_json === 'string'
              ? JSON.parse(response.omr_processed_json)
              : response.omr_processed_json;
  
            if (response.template) {
              let templateData = typeof response.template === 'string'
                ? JSON.parse(response.template)
                : response.template;
              this.templateData = templateData;
            }
  
            this.omrResponseData = processedData;
            this.handleStudentIdentification();
  
            if (!this.omrResponseData.TestId) {
              this.omrResponseData.TestId = metadata.test_id;
            }
          } else {
            throw new Error("No OMR processed data found");
          }
        } catch (error) {
          console.error("Error fetching OMR data:", error);
          this.omrResponseData = {
            TestId: this.testData?.test_id,
            Set: "1",
            Roll: this.selectedOmr?.student_id
          };
        } finally {
          this.$root.showLoader = false;
        }
      },
  
      updateOmrResponses() {
        if (!this.omrResponseData) return;
  
        const requestData = {
          id: this.omrId,
          test_id: this.testData.test_id,
          omr_processed_json: JSON.parse(JSON.stringify(this.omrResponseData))
        };
  
        if (requestData.omr_processed_json.TestId && this.isTestIdEdited) {
          requestData.test_id = requestData.omr_processed_json.TestId;
        }
  
        if (requestData.omr_processed_json.Roll) {
          requestData.roll_number = requestData.omr_processed_json.Roll;
        }
  
        this.updatingOmr = true;
        this.editOmrResponseData(requestData)
          .then(response => {
            if (response) {
              this.$toasted.success("OMR responses updated successfully, Processing starts in 7 minutes.");
              this.closeDialog();
            } else {
              throw new Error('Update failed');
            }
          })
          .catch(error => {
            console.error("Error updating OMR responses:", error);
            // this.$toasted.error("Failed to update OMR responses");
          })
          .finally(() => {
            this.updatingOmr = false;
          });
      },
  
      selectOption(questionKey, option) {
        // Set the selected option for the question
        this.$set(this.omrResponseData, questionKey, option);
      },
      getQuestionsForSection(section) {
        // Get total questions from omrResponseData dynamically
        const questionKeys = Object.keys(this.omrResponseData || {}).filter(key => key.startsWith('q'));
        const totalQuestions = Math.max(...questionKeys.map(key => parseInt(key.substring(1)))) || 0;
  
        // Split questions into 4 columns
        const questionsPerColumn = Math.ceil(totalQuestions / 4);
        const startNum = (section - 1) * questionsPerColumn + 1;
        const endNum = Math.min(section * questionsPerColumn, totalQuestions);
  
        return Array.from(
          { length: endNum - startNum + 1 },
          (_, i) => startNum + i
        );
      },
      isOptionSelected(questionNum, option) {
        const questionKey = `q${questionNum}`;
        const value = this.omrResponseData[questionKey] || '';
        return value.includes(option);
      },
      toggleOption(questionNum, option) {
        if (this.isTestIdEdited) return; // Prevent changes if TestId is edited
  
        const questionKey = `q${questionNum}`;
        let currentValue = this.omrResponseData[questionKey] || '';
  
        if (currentValue.includes(option)) {
          currentValue = currentValue.replace(option, '');
        } else {
          currentValue += option;
        }
  
        currentValue = Array.from(currentValue).sort().join('');
        this.$set(this.omrResponseData, questionKey, currentValue);
      },
      isMultipleChoiceQuestion(questionKey) {
        if (!this.omrResponseData) return true;
  
        const value = this.omrResponseData[questionKey];
  
        // If value is empty and we have template data, check customLabels
        if ((!value || value === '') && this.templateData && this.templateData.customLabels) {
          if (this.templateData.customLabels[questionKey]) {
            return false;
          }
        }
  
        // If value exists, check if it's in MCQ format (A,B,C,D)
        return !value || /^[ABCD]*$/.test(value);
      },
      handleTestIdChange(value) {
        if (!this.originalTestId) {
          this.originalTestId = this.tempTestId;
        }
        this.isTestIdEdited = value !== this.originalTestId;
      },
      startTestIdEdit() {
        this.isTestIdEditing = true;
        this.tempTestId = this.omrResponseData.TestId;
        this.originalTestId = this.omrResponseData.TestId;
      },
      saveTestId() {
        this.isTestIdEditing = false;
        // Keep isTestIdEdited true if value changed
        this.isTestIdEdited = this.omrResponseData.TestId !== this.originalTestId;
      },
      cancelTestIdEdit() {
        this.isTestIdEditing = false;
        this.omrResponseData.TestId = this.tempTestId;
        this.isTestIdEdited = false;
      },
      closeDialog() {
        this.showDialog = false;
        this.omrResponseData = null;
        this.originalTestId = null;
        this.isTestIdEdited = false;
        this.isTestIdEditing = false;
        this.tempTestId = null;
        this.templateData = null;
        this.$emit('close');
      },
      handleStudentIdentification() {
        const studentId = parseInt(this.selectedOmr?.student_id);
  
        const existingRoll = parseInt(this.omrResponseData['Roll']);
        const existingStudentId = parseInt(this.omrResponseData['RollOG']);
  
        if (!existingRoll && !existingStudentId) {
          // No IDs exist - use student_id as StudentId
          this.haveStudentIDOnly = true;
          this.omrResponseData['Roll'] = studentId;
        } else if ( (existingStudentId && existingStudentId !== studentId) || (existingRoll && existingRoll !== studentId)) {
          // Either ID exists and differs from student_id - treat as Roll
          this.haveStudentIDOnly = false;
          this.omrResponseData['Roll'] = existingStudentId || existingRoll;
        } else {
          // IDs match student_id - use as StudentId
          this.haveStudentIDOnly = true;
          this.omrResponseData['Roll'] = studentId;
        }
      },
    },
    watch: {
      omrId: {
        immediate: true,
        handler(newVal) {
          if (newVal) {
            this.fetchOmrData();
          }
        }
      }
    }
  };
  </script>
  